import { FiFileText } from "react-icons/fi";
import { HiStar } from "react-icons/hi";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";
import TenantSlugs from "../../../utils/TenantSlug";

const SCHEME_TYPE_NAMES = { 1: "All Products", 2: "Classification", 3: "Subcategory-Category" };
const TIER_TYPE_NAMES = { "1": "Points", "2": "Weight" };
const KEY_LABELS = {
  qr_mrp: "QR MRP",
  product_mrp: "Product MRP",
  product_points: "Product Points",
  qr_original_mrp: "QR Original MRP",
};
const GIFT_TYPE_COLORS = {
  cash: "bg-green-50 text-green-700 border-green-200",
  gift: "bg-purple-50 text-purple-700 border-purple-200",
  gift_as_cashback: "bg-blue-50 text-blue-700 border-blue-200",
};

// Parses PostgreSQL array literals like {"gift","gift_as_cashback"} or {"cash"}
function parsePgArray(val) {
  if (Array.isArray(val)) return val;
  if (!val || typeof val !== "string") return [];
  const inner = val.replace(/^\{|\}$/g, "").trim();
  if (!inner) return [];
  return inner.split(",").map((s) => s.replace(/^"|"$/g, "").trim()).filter(Boolean);
}

function getUserTypeName(id) {
  try {
    const types = JSON.parse(sessionStorage.getItem("appUserType")) || [];
    return types.find((t) => String(t.user_type_id) === String(id))?.name || String(id);
  } catch { return String(id); }
}

function fmtDate(raw) {
  if (!raw) return "—";
  const d = (raw || "").split("T")[0];
  const [y, m, day] = (d || "").split("-");
  return y && m && day ? `${day}/${m}/${y}` : "—";
}

// ── primitives ────────────────────────────────────────────────────────────────

const SectionTitle = ({ children }) => (
  <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">{children}</span>
  </div>
);

const Row = ({ label, children }) => (
  <div className="flex items-start gap-3 px-4 py-2 border-b border-gray-100 last:border-0">
    <span className="text-xs text-gray-400 font-medium shrink-0 w-36 pt-0.5 leading-relaxed">{label}</span>
    <div className="text-sm text-gray-800 flex-1 min-w-0">{children ?? <span className="text-gray-300">—</span>}</div>
  </div>
);

const Chip = ({ children, className = "" }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border capitalize ${className || "bg-gray-100 text-gray-600 border-gray-200"}`}>
    {children}
  </span>
);

const OnOff = ({ on, onText = "On", offText = "Off" }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold border ${on ? "bg-green-50 text-green-700 border-green-200" : "bg-gray-50 text-gray-400 border-gray-200"}`}>
    <span className={`w-1.5 h-1.5 rounded-full ${on ? "bg-green-500" : "bg-gray-300"}`} />
    {on ? onText : offText}
  </span>
);

const ChipList = ({ items, className }) =>
  items.length === 0 ? <span className="text-gray-300 text-xs">—</span> : (
    <div className="flex flex-wrap gap-1">{items.map((v) => <Chip key={v} className={className}>{v}</Chip>)}</div>
  );

// ── main ──────────────────────────────────────────────────────────────────────

const ViewSchemeModal = ({ show, setShow, scheme, giftCataloguesList = [] }) => {
  if (!scheme) return null;

  const tenantId = JSON.parse(sessionStorage.getItem("vendorData") || "{}").tenant_id;
  const isSeedworks = String(tenantId) === String(TenantSlugs.seedworks);

  const settings = scheme.settings || {};
  const userTypePoints = settings.user_type_points || {};
  const detailImage = scheme.detail_image || scheme.detailImage;

  const catalogueName =
    giftCataloguesList.find((c) =>
      String(c.catalogue_id) === String(scheme.gift_catalogue_id) ||
      String(c.id) === String(scheme.gift_catalogue_id)
    )?.catalogue_name ||
    giftCataloguesList.find((c) =>
      String(c.catalogue_id) === String(scheme.gift_catalogue_id) ||
      String(c.id) === String(scheme.gift_catalogue_id)
    )?.name ||
    (scheme.gift_catalogue_id ? `Catalogue #${scheme.gift_catalogue_id}` : "—");

  const userTypeNames = (scheme.user_types || []).map(getUserTypeName);
  const states = scheme.states || [];
  const products = scheme.products || [];
  const giftTypes = parsePgArray(scheme.gift_type);

  const isActive =
    scheme.scheme_start && scheme.scheme_end &&
    new Date() >= new Date(scheme.scheme_start) &&
    new Date() <= new Date(scheme.scheme_end);
  const isExpired = scheme.scheme_end && new Date() > new Date(scheme.scheme_end);

  return (
    <ModalComponent size="4xl" show={show} setShow={setShow} showClose>
      <div className="flex flex-col" style={{ maxHeight: "86vh" }}>

        {/* ── Header ── */}
        <div className="px-4 pt-4 pb-3 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                {isActive ? (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-50 text-green-700 border border-green-200">
                    <span className="w-1 h-1 rounded-full bg-green-500" />Active
                  </span>
                ) : isExpired ? (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-600 border border-red-200">
                    <span className="w-1 h-1 rounded-full bg-red-400" />Expired
                  </span>
                ) : (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-500 border border-gray-200">Upcoming</span>
                )}
                {scheme.is_primary && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                    <HiStar className="w-3 h-3 fill-amber-400" />Primary
                  </span>
                )}
              </div>
              <h2 className="text-base font-semibold text-gray-900 leading-snug">{scheme.name}</h2>
            </div>
            <div className="text-right shrink-0">
              {scheme.created_by_name && (
                <p className="text-xs text-gray-500">
                  <span className="text-gray-400">By </span>{scheme.created_by_name}
                </p>
              )}
              {scheme.created_at && (
                <p className="text-[11px] text-gray-400">{new Date(scheme.created_at).toLocaleString()}</p>
              )}
              {scheme.id && (
                <p className="text-[10px] text-gray-300 font-mono mt-0.5">#{scheme.id}</p>
              )}
            </div>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="overflow-y-auto flex-1">

          {/* Section 1 – Basic Info */}
          <SectionTitle>Basic Info</SectionTitle>

          <Row label="User Types">
            <div className="flex flex-wrap gap-1">
              {userTypeNames.length > 0
                ? userTypeNames.map((n) => <Chip key={n} className="bg-indigo-50 text-indigo-700 border-indigo-100">{n}</Chip>)
                : <span className="text-gray-300 text-xs">—</span>}
            </div>
          </Row>

          <Row label="Scheme Period">
            <span className="font-mono text-sm">{fmtDate(scheme.scheme_start)}</span>
            <span className="mx-2 text-gray-300">→</span>
            <span className="font-mono text-sm">{fmtDate(scheme.scheme_end)}</span>
          </Row>

          <Row label="Primary Scheme">
            {scheme.is_primary
              ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700"><HiStar className="w-3.5 h-3.5 fill-amber-400" />Yes</span>
              : <span className="text-xs text-gray-400">No</span>}
          </Row>

          {scheme.description && (
            <Row label="Description">
              <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{scheme.description}</p>
            </Row>
          )}

          {(scheme.image || detailImage || scheme.pdf) && (
            <Row label="Media">
              <div className="flex items-end gap-3">
                {scheme.image && (
                  <div className="flex flex-col items-center gap-1">
                    <img src={scheme.image} alt="Banner" className="h-16 w-16 object-cover rounded border border-gray-200" />
                    <span className="text-[10px] text-gray-400">Banner</span>
                  </div>
                )}
                {detailImage && (
                  <div className="flex flex-col items-center gap-1">
                    <img src={detailImage} alt="Detail" className="h-16 w-16 object-cover rounded border border-gray-200" />
                    <span className="text-[10px] text-gray-400">Detail</span>
                  </div>
                )}
                {scheme.pdf && (
                  <a href={scheme.pdf} target="_blank" rel="noreferrer"
                    className="flex flex-col items-center justify-center h-16 w-16 rounded border-2 border-dashed border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors gap-1">
                    <FiFileText className="h-5 w-5" />
                    <span className="text-[10px] font-medium">PDF</span>
                  </a>
                )}
              </div>
            </Row>
          )}

          {/* Section 2 – Filters & Products */}
          <SectionTitle>Filters &amp; Products</SectionTitle>

          <Row label="Scheme Type">
            <Chip className="bg-blue-50 text-blue-700 border-blue-100">
              {SCHEME_TYPE_NAMES[Number(scheme.type)] || `Type ${scheme.type}`}
            </Chip>
          </Row>

          <Row label="User Restriction">
            <OnOff
              on={settings.type === "scheme_id_check"}
              onText="Assigned users only"
              offText="All eligible users"
            />
          </Row>

          <Row label={`States (${states.length})`}>
            {states.length > 0
              ? <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto"><ChipList items={states} /></div>
              : <span className="text-gray-300 text-xs">—</span>}
          </Row>

          <Row label={`Products (${Number(scheme.type) === 1 ? "all" : products.length})`}>
            {Number(scheme.type) === 1
              ? <span className="text-xs text-blue-700 bg-blue-50 border border-blue-100 px-2 py-0.5 rounded">All Active Products</span>
              : products.length > 0
                ? <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                    {products.map((c) => <Chip key={c} className="bg-gray-100 text-gray-600 border-gray-200 font-mono">{c}</Chip>)}
                  </div>
                : <span className="text-gray-300 text-xs">—</span>}
          </Row>

          {/* Section 3 – Points & Rewards */}
          <SectionTitle>Points &amp; Rewards</SectionTitle>

          <Row label="Gift Catalogue">{catalogueName}</Row>

          <Row label="Gift Types">
            {giftTypes.length > 0
              ? <div className="flex flex-wrap gap-1">
                  {giftTypes.map((gt) => (
                    <Chip key={gt} className={GIFT_TYPE_COLORS[gt] || "bg-gray-100 text-gray-600 border-gray-200"}>{gt}</Chip>
                  ))}
                </div>
              : <span className="text-gray-300 text-xs">—</span>}
          </Row>

          {isSeedworks && (
            <Row label="Tier Type">
              {TIER_TYPE_NAMES[String(scheme.tier_type)] || "—"}
            </Row>
          )}

          <Row label="Custom Formulas">
            {Object.keys(userTypePoints).length === 0
              ? <span className="text-xs text-gray-400">None — base company points apply</span>
              : <div className="space-y-1 w-full">
                  {Object.entries(userTypePoints).map(([utKey, cfg]) => (
                    <div key={utKey} className="flex flex-wrap items-center gap-1.5 bg-gray-50 border border-gray-200 rounded px-2.5 py-1.5">
                      <Chip className="bg-indigo-50 text-indigo-700 border-indigo-100">{utKey}</Chip>
                      <span className="text-[11px] text-gray-400">key =</span>
                      <code className="text-[11px] font-mono bg-white border border-gray-200 px-1 rounded text-gray-700">{KEY_LABELS[cfg.key] || cfg.key || "—"}</code>
                      <span className="text-gray-300 text-[11px]">→</span>
                      <code className="text-[11px] font-mono bg-white border border-gray-200 px-1 rounded text-gray-800">{cfg.parser || "—"}</code>
                      {cfg.db_column && (
                        <span className="text-[10px] text-amber-700 font-mono bg-amber-50 border border-amber-200 px-1 rounded">
                          {cfg.db_table || "products"}.{cfg.db_column}
                        </span>
                      )}
                    </div>
                  ))}
                </div>}
          </Row>

          {/* Section 4 – Redemption & TDS */}
          <SectionTitle>Redemption &amp; TDS</SectionTitle>

          <Row label="Redeem Period">
            <span className="font-mono text-sm">{fmtDate(scheme.redeem_start)}</span>
            <span className="mx-2 text-gray-300">→</span>
            <span className="font-mono text-sm">{fmtDate(scheme.redeem_end)}</span>
          </Row>

          <Row label="TDS Redemption">
            <OnOff on={settings.tds_redeem ?? true} onText="On" offText="Off" />
            <span className="ml-2 text-[11px] text-gray-400">deduct TDS on redemptions</span>
          </Row>

          <Row label="TDS Calculation">
            <OnOff on={settings.tds_calc ?? false} onText="On" offText="Off" />
            <span className="ml-2 text-[11px] text-gray-400">manual reserve calc at scheme end</span>
          </Row>

        </div>

        {/* ── Footer ── */}
        <div className="px-4 py-2.5 border-t border-gray-200 bg-gray-50 flex items-center justify-between flex-shrink-0">
          <span className="text-[11px] text-gray-400 font-mono">ID: {scheme.id || "—"}</span>
          <button
            type="button"
            onClick={() => setShow(false)}
            className="px-4 py-1.5 rounded bg-gray-800 hover:bg-gray-900 text-white text-xs font-medium transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </ModalComponent>
  );
};

export default ViewSchemeModal;
